import type { ItemFieldRow } from './item-field-reader';
import { readInventoryField } from './inventory-raw-readers';
import { phpJsonString, phpJsonTruthy } from './php-json-values';
import { phpJsonInteger } from './php-integer-arithmetic';
import { phpJsonFloat } from './php-decimal-values';
import { normalizeInventoryTransform } from './inventory-transform';
import { normalizeInventoryGeneration } from './inventory-generation';
import { selectInventoryVitalitySource } from './vitality-reader';
import { applyTreeSizeRules } from './tree-size-rules';

const arrayLike = (value: unknown): value is ItemFieldRow => value !== null && typeof value === 'object';
const has = (value: unknown, key: string): boolean => arrayLike(value) && Object.prototype.hasOwnProperty.call(value, key);
const own = (value: unknown, key: string): unknown => has(value, key) ? (value as Readonly<Record<string, unknown>>)[key] : undefined;
function clone(value: unknown): unknown {
    if (!arrayLike(value)) return value ?? null;
    const entries = Object.entries(value);
    return entries.every(([key], index) => key === String(index)) ? entries.map(([, child]) => clone(child))
        : Object.fromEntries(entries.map(([key, child]) => [key, clone(child)]));
}
function writable(value: unknown): Record<string, unknown> {
    if (value == null || value === false) return {};
    if (!arrayLike(value)) throw new TypeError('Cannot write inventory array offsets on a scalar');
    return Object.fromEntries(Object.entries(value));
}
export class MissingInventoryColumn extends Error {
    constructor(public readonly column: string) { super(`Unavailable inventory SQL column: ${column}`); this.name = 'MissingInventoryColumn'; }
}
function column(row: ItemFieldRow, key: string): unknown {
    if (!has(row, key)) throw new MissingInventoryColumn(key);
    return own(row, key);
}
function stringArgument(value: unknown): string {
    if (value == null || arrayLike(value)) throw new TypeError('Inventory lookup requires a scalar string argument');
    return phpJsonString(value);
}
/** build_pos_arr over supplied SQL axes or the stored EC-axis fallback. Missing
 * SQL columns are unavailable inputs; non-finite output cannot enter encounter JSON.
 * The old pos_arr key is not removed by this PHP stage.
 */
export function buildPosition(target: ItemFieldRow, source: ItemFieldRow | null = null, warn?: () => void): ItemFieldRow {
    const result = writable(clone(target)), axes = ['x_pos', 'y_pos', 'z_pos'];
    const ec = own(target, 'encounter_creatures');
    const input = phpJsonTruthy(source) ? source : axes.every(key => own(ec, key) != null) ? ec as ItemFieldRow : null;
    if (input === null) { warn?.(); return clone(result) as ItemFieldRow; }
    const position: Record<string, number> = {};
    // PHP reads all three call arguments before the closure casts any axis.
    const coordinates = axes.map(axis => column(input, axis));
    for (const [index, axis] of axes.entries()) {
        const value = phpJsonFloat(coordinates[index]);
        if (!Number.isFinite(value)) throw new RangeError('Position is not JSON-representable');
        if (value !== 0) position[axis] = value;
    }
    if (Object.keys(position).length) result.position = position; else delete result.position;
    return clone(result) as ItemFieldRow;
}
export function resolveInventoryPossessorContext(target: ItemFieldRow, knownType: unknown,
    determineType: (id: string) => unknown): ItemFieldRow {
    // PHP applies the nullable-string argument conversion before entering the body.
    const known = knownType == null ? null : stringArgument(knownType);
    const id = readInventoryField(target, 'possess_id');
    const type = !phpJsonTruthy(id) ? 'root' : known ?? stringArgument(determineType(stringArgument(id)));
    const result = writable(clone(target)), computed = writable(own(result, 'computed'));
    const possessor = writable(own(computed, 'possessor'));
    possessor.type = type; computed.possessor = possessor; result.computed = computed;
    return clone(result) as ItemFieldRow;
}
export interface InventoryInitializationDependencies {
    resolveTemplate: (id: string) => ItemFieldRow | null;
    resolveItemType: (id: unknown) => unknown;
    determinePossessorType: (id: string) => unknown;
    tinySizeId: string;
    hasLayer: (row: ItemFieldRow, key: string) => boolean;
    readVitality: (row: ItemFieldRow) => ItemFieldRow | null;
    compileVitality: (row: ItemFieldRow, source: ItemFieldRow) => { row: ItemFieldRow; burn: boolean };
    positionWarning?: () => void;
}
/** Actual build_inventory_arr prefix through position, before size/enchantment/
 * status/container/permission/picture stages. The returned row is an intermediate,
 * not a complete inventory. Temporary root mirrors must survive until final cleanup.
 */
export function initializeInventoryCompile(row: ItemFieldRow, context: ItemFieldRow, deps: InventoryInitializationDependencies): ItemFieldRow {
    const itemId = column(row, 'inventory_item_id');
    let result = deps.resolveTemplate(stringArgument(itemId));
    if (!phpJsonTruthy(result)) {
        const type = own(row, 'item_type') ?? deps.resolveItemType(itemId);
        result = { items: { item_id: itemId, item_type: phpJsonTruthy(type) ? type : 'potion',
            item_name: own(row, 'true_inventory_name') ?? 'Unknown', item_description: own(row, 'item_description') ?? '',
            flavor_text: own(row, 'flavor_text') ?? '' }, item_layer_types: { size: deps.tinySizeId } };
    }
    const out = writable(clone(applyTreeSizeRules(result as ItemFieldRow, row, deps.resolveTemplate)));
    out.inventory_id = column(row, 'inventory_id');
    out.inventory_created_by_id = column(row, 'inventory_created_by_id');
    for (const key of ['inventory_deleted', 'inventory_private']) if (phpJsonTruthy(own(row, key))) out[key] = own(row, key);
    out.inventory_name = column(row, 'true_inventory_name');
    out.item_type = column(row, 'item_type');
    out.inventory_description = deps.hasLayer(row, 'inventory_description') ? column(row, 'inventory_description') : column(row, 'item_description');
    out.inventory_written_in_language = column(row, 'inventory_written_in_language');
    out.inventory_flavor_text = deps.hasLayer(row, 'inventory_flavor_text') ? column(row, 'inventory_flavor_text') : column(row, 'flavor_text');
    out.inventory_gm_notes = column(row, 'inventory_gm_notes');
    out.inventory_item_id = column(row, 'inventory_item_id');
    out.inventory_cost_gp = column(row, 'inventory_cost_gp');
    out.inventory_size = own(row, 'inventory_size') ?? null;
    out.weight_override = own(row, 'weight_override') ?? null;
    for (const key of ['item_qty', 'equipped', 'enchantment_template_id']) out[key] = column(row, key);
    if (phpJsonTruthy(own(row, 'inventory_unidentified'))) out.inventory_unidentified = own(row, 'inventory_unidentified');
    out.inventory_inspection_dc = column(row, 'inventory_inspection_dc');
    out.lockpick_degree_start = own(row, 'lockpick_degree_start') ?? null;
    out.lockpick_degree_end = own(row, 'lockpick_degree_end') ?? null;
    out.possess_id = column(row, 'possess_id');
    const inventory: Record<string, unknown> = { inventory_id: column(row, 'inventory_id'),
        inventory_created_by_id: column(row, 'inventory_created_by_id'), inventory_name: column(row, 'true_inventory_name'),
        inventory_item_id: column(row, 'inventory_item_id'), item_qty: column(row, 'item_qty'), possess_id: column(row, 'possess_id') };
    out.inventory = inventory;
    const description = deps.hasLayer(row, 'inventory_description') ? column(row, 'inventory_description') : own(row, 'item_description') ?? null;
    if (description !== null && description !== '') inventory.inventory_description = description;
    for (const key of ['inventory_written_in_language', 'inventory_gm_notes', 'inventory_cost_gp', 'inventory_size']) {
        if (phpJsonTruthy(own(row, key))) inventory[key] = own(row, key);
    }
    if (own(row, 'weight_override') != null) inventory.weight_override = own(row, 'weight_override');
    // inventory_encounter_creature_id — the CORPSE POINTER (build_inventory_arr.php twin, 2026-09-23). Dropping it here
    // re-projects a browser-compiled body as the plain corpse template (its humanoid mesh) instead of the creature's look.
    for (const key of ['equipped', 'enchantment_template_id', 'mesh_id', 'inventory_encounter_creature_id']) if (phpJsonTruthy(own(row, key))) inventory[key] = own(row, key);
    if (has(row, 'transform_3d')) {
        const transform = normalizeInventoryTransform(own(row, 'transform_3d'));
        if (transform !== null) inventory.transform_3d = transform;
    }
    // DAFT3D S8 generation record (build_inventory_arr.php twin): elided when NULL or invalid, so a manual row is unchanged.
    if (has(row, 'inventory_generation')) {
        const generation = normalizeInventoryGeneration(own(row, 'inventory_generation'));
        if (generation !== null) inventory.inventory_generation = generation;
    }
    const flavor = deps.hasLayer(row, 'inventory_flavor_text') ? column(row, 'inventory_flavor_text') : own(row, 'flavor_text') ?? null;
    if (flavor !== null && flavor !== '') inventory.inventory_flavor_text = flavor;
    const dc = own(row, 'inventory_inspection_dc');
    if (phpJsonTruthy(dc) && phpJsonInteger(dc) !== 10n) inventory.inventory_inspection_dc = dc;
    for (const key of ['lockpick_degree_start', 'lockpick_degree_end', 'inventory_deleted', 'inventory_private', 'inventory_unidentified', 'inventory_wares']) {
        if (phpJsonTruthy(own(row, key))) inventory[key] = own(row, key);
    }
    const positionedOwner = resolveInventoryPossessorContext(out, own(context, 'possessor_type') ?? null, deps.determinePossessorType);
    const vitalitySource = selectInventoryVitalitySource(row, positionedOwner, deps.readVitality);
    const compiled = deps.compileVitality(positionedOwner, vitalitySource);
    return buildPosition(compiled.row, row, deps.positionWarning);
}
